/**
 * `<HeartButton />` — Story 5.4 (UX §3.1 line 270, AC line 1009).
 *
 * Voter upvote button. Story 5.4 owns the visual + tap behavior; it does
 * NOT own the API call, the optimistic state, or the toast firing — those
 * live at the consumer surface (Stories 9.4 / 9.5 / 9.7).
 *
 * Tap area: 56×56 px (UX §5.2 line 1007 — exceeds the 44px universal floor).
 *
 * States:
 *   - `idle`   — heart outline, `var(--upvote-idle)`.
 *   - `active` — heart filled, `var(--upvote-active)` (= `var(--brand-primary)`).
 *
 * Animation:
 *   - On `idle → active` transition: scale 1.0 → 1.18 → 1.0 over 240ms
 *     (`--motion-base × 1.2`). CSS `@keyframes` emitted via top-of-module
 *     `<style>` block; idempotent (same name + same rule across multiple
 *     mounts is a no-op for the browser). Reduced-motion override disables
 *     the animation entirely (UX §2.6 line 211).
 *
 * Optimistic + revert pattern (LOCKED contract; consumer-owned state):
 *   - Parent maintains `isActive: boolean`.
 *   - On click → parent calls `onActivate()` → parent flips `isActive=true`
 *     → parent fires API → on error, parent flips `isActive=false` AND
 *     fires its own Toast.
 *
 * Self-vote-disabled state:
 *   - `disabled` — heart at 40% opacity, no callback fires, custom
 *     `aria-label="Voters can't upvote their own photo"`.
 *
 * Aria-live announcement:
 *   - The parent surface owns the `aria-live="polite"` region per
 *     UX §5.2 line 1008. HeartButton emits no live-region updates.
 */
import { useEffect, useRef, useState } from 'react';

const TAP_TARGET_PX = 56;
const ANIMATION_NAME = 'pvt-heart-pulse';
const ANIMATION_DURATION_MS = 240;

const ANIMATION_STYLE = `
@keyframes ${ANIMATION_NAME} {
  0% { transform: scale(1.0); }
  50% { transform: scale(1.18); }
  100% { transform: scale(1.0); }
}
@media (prefers-reduced-motion: reduce) {
  .${ANIMATION_NAME}-animate {
    animation: none !important;
  }
}
`;

export interface HeartButtonProps {
  isActive: boolean;
  disabled?: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  ariaLabel?: string;
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width={28}
      height={28}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-testid={filled ? 'heart-active' : 'heart-idle'}
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

/**
 * `HeartButton` — voter upvote toggle.
 *
 * @param isActive      Source-of-truth from parent's optimistic state.
 * @param disabled      When true, self-vote-blocked rendering.
 * @param onActivate    Fired on click when previously idle.
 * @param onDeactivate  Fired on click when previously active.
 * @param ariaLabel     Custom aria-label override (defaults vary by state).
 */
export function HeartButton({
  isActive,
  disabled = false,
  onActivate,
  onDeactivate,
  ariaLabel,
}: HeartButtonProps) {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const prevActive = useRef(isActive);

  useEffect(() => {
    // Only animate on transition into active state.
    if (!prevActive.current && isActive) {
      setShouldAnimate(true);
      const timer = window.setTimeout(() => setShouldAnimate(false), ANIMATION_DURATION_MS);
      prevActive.current = isActive;
      return () => window.clearTimeout(timer);
    }
    prevActive.current = isActive;
  }, [isActive]);

  const handleClick = () => {
    if (disabled) return;
    if (isActive) {
      onDeactivate();
    } else {
      onActivate();
    }
  };

  const computedAriaLabel = disabled
    ? "Voters can't upvote their own photo"
    : ariaLabel ?? (isActive ? 'Remove upvote' : 'Upvote');

  return (
    <>
      <style data-testid="heart-keyframes">{ANIMATION_STYLE}</style>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-pressed={isActive}
        aria-disabled={disabled || undefined}
        aria-label={computedAriaLabel}
        className={shouldAnimate ? `${ANIMATION_NAME}-animate` : undefined}
        style={{
          minWidth: TAP_TARGET_PX,
          minHeight: TAP_TARGET_PX,
          background: 'transparent',
          color: isActive ? 'var(--upvote-active)' : 'var(--upvote-idle)',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          animation: shouldAnimate
            ? `${ANIMATION_NAME} ${ANIMATION_DURATION_MS}ms ease-out`
            : undefined,
          outlineWidth: 2,
          outlineStyle: 'solid',
          outlineColor: 'var(--brand-primary)',
          outlineOffset: 2,
        }}
      >
        <HeartIcon filled={isActive} />
      </button>
    </>
  );
}
